<template>
  <div class="d-inline-flex">
    <v-menu v-model="menu" offset-y left :close-on-content-click="false" max-width="380" min-width="320">
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon v-bind="attrs" v-on="on">
          <v-badge :value="total_unread > 0" :content="total_unread > 99 ? '99+' : total_unread" color="red" overlap>
            <v-icon>mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-card>
        <v-card-title class="py-2 subtitle-1">
          Notificaciones
          <v-spacer></v-spacer>
          <v-btn v-if="unread > 0" text x-small color="primary" @click="readAll">Marcar todas como leídas</v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <!-- Las novedades que se dieron por leidas al abrir la campana -->
        <template v-if="news_in_menu.length > 0">
          <v-list dense class="py-0">
            <v-subheader>
              Novedades
              <v-spacer></v-spacer>
              <a href="#" @click.prevent="openNews">Ver todas</a>
            </v-subheader>
            <v-list-item v-for="item in news_in_menu" :key="'news-' + item.id" @click="openNews">
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ item.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider></v-divider>
        </template>

        <div v-if="list.length == 0 && news_in_menu.length == 0" class="pa-6 text-center grey--text">No tenés notificaciones.</div>
        <v-list v-else-if="list.length > 0" dense style="max-height: 420px; overflow-y: auto">
          <v-list-item v-for="item in list" :key="item.id" @click="open(item)" :class="{ 'blue lighten-5': !item.read }">
            <v-list-item-content>
              <v-list-item-title :class="{ 'font-weight-bold': !item.read }">{{ item.title }}</v-list-item-title>
              <div class="caption">{{ item.body }}</div>
              <div class="caption grey--text">{{ formatDateTime(item.creation_date) }}</div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Fuera del v-menu: su contenido no se renderiza hasta abrirlo -->
    <NewsNotice :value="show_news_notice" :count="news_count" :latest="news_latest" :marking="news_marking" @read="markNewsRead" @open="openNews" />
  </div>
</template>

<script>
import { notificationService } from "@/libs/ws/notificationService";
import { newsService } from "@/libs/ws/newsService";
import { authService } from "@/libs/ws/authService";
import NewsNotice from "@/components/NewsNotice.vue";
import Constant from "@/constants/sections";

const REFRESH_MS = 60000;
const NEWS_PATH = "/" + Constant.SECTION_NEWS;

export default {
  name: "NotificationBell",
  components: { NewsNotice },
  data() {
    return {
      menu: false,
      list: [],
      unread: 0,
      timer: null,
      news_count: 0,
      news_latest: [],
      // Lo que se marco como leido al abrir la campana, para mostrarlo mientras siga abierta
      news_in_menu: [],
      news_loaded: false,
      news_marking: false,
      show_news_notice: false,
    };
  },
  computed: {
    total_unread() {
      return this.unread + this.news_count;
    },
  },
  mounted() {
    this.load();
    this.timer = setInterval(this.load, REFRESH_MS);
    this.$root.$on("news-read", this.clearNews);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.$root.$off("news-read", this.clearNews);
  },
  watch: {
    menu(value) {
      if (value) {
        this.load();
        // Tocar la campana es darse por enterado de las novedades
        if (this.news_count > 0) {
          this.news_in_menu = this.news_latest.slice();
          this.markNewsRead();
        }
      } else {
        this.news_in_menu = [];
      }
    },
    "$route.path"(path) {
      if (path == NEWS_PATH) this.show_news_notice = false;
      // Con la contraseña pendiente no se consulta: el aviso sale al terminar ese paso
      else if (!this.news_loaded) this.loadNews();
    },
  },
  methods: {
    load() {
      this.loadNews();
      notificationService
        .filter()
        .then((response) => {
          if (response.status) {
            this.list = response.result.list;
            this.unread = response.result.unread;
          }
        })
        .catch((e) => console.log(e));
    },
    open(item) {
      if (!item.read) {
        item.read = true;
        this.unread = Math.max(0, this.unread - 1);
        notificationService.read(item.id).catch((e) => console.log(e));
      }
      this.menu = false;
      // Solo navega si la ruta existe en el frontend
      if (item.link && this.$router.resolve(item.link).route.matched.length > 0 && this.$route.fullPath != item.link) {
        this.$router.push(item.link);
      }
    },
    readAll() {
      notificationService
        .read_all()
        .then(() => {
          this.list.forEach((item) => (item.read = true));
          this.unread = 0;
        })
        .catch((e) => console.log(e));
    },
    // El aviso sale al entrar al sistema y, despues, solo si llega una novedad que no estaba antes
    loadNews() {
      if (authService.mustChangePassword()) return;

      const first_load = !this.news_loaded;
      const previous_ids = this.news_latest.map((item) => item.id);
      newsService
        .unread()
        .then((response) => {
          if (!response.status) return;
          this.news_loaded = true;
          this.setNews(response.result.count, response.result.latest);

          if (this.news_count == 0 || this.$route.path == NEWS_PATH) return;
          if (first_load || this.news_latest.some((item) => !previous_ids.includes(item.id))) this.show_news_notice = true;
        })
        .catch((e) => console.log(e));
    },
    setNews(count, latest) {
      this.news_count = count;
      this.news_latest = latest;
      this.$store.commit("setNewsUnread", count);
    },
    clearNews() {
      this.setNews(0, []);
      this.show_news_notice = false;
    },
    markNewsRead() {
      if (this.news_marking) return;
      this.news_marking = true;
      newsService
        .readAll()
        .then((response) => {
          if (response.status) this.clearNews();
          else this.$toast.error(response.msg, "Error");
        })
        .catch(() => this.$toast.error("No se pudieron marcar las novedades como leídas.", "Error"))
        .finally(() => (this.news_marking = false));
    },
    openNews() {
      this.show_news_notice = false;
      this.menu = false;
      if (this.$route.path != NEWS_PATH) this.$router.push(NEWS_PATH);
    },
    formatDateTime(date) {
      const [day_part, time_part] = date.split(" ");
      const [year, month, day] = day_part.split("-");
      return `${day}/${month}/${year} ${time_part.substr(0, 5)}`;
    },
  },
};
</script>
